/**
 * Leviathan captain creation.
 *
 * Encodes docs/rules/00-attributes.md, 01-qualities.md and 02-creation.md.
 * The documents are authoritative; this package is their machine-readable
 * form. When they change, change these files and bump `version`.
 *
 * Also encodes 03-factions.md, 04-ranks.md, 05-essence.md and 06-reputation.md.
 * Factions and Reputation are shared with Interceptor and live in
 * @personnel/setting; only the Cost-keyed Recommended Renown table is here.
 *
 * Not yet covered by the rules documents, and therefore not implemented:
 * advancement, and the Total XP ledger described in 05-essence.md's notes.
 */
import type { RulesPack } from "@personnel/core";
import { createBlankCaptain, leviathanCaptainSchema } from "./character.js";
import type { LeviathanCaptain } from "./character.js";
import { derive } from "./engine/derive.js";
import type { LeviathanDerived } from "./engine/derive.js";
import { validate } from "./engine/validate.js";

export const leviathanRules: RulesPack<LeviathanCaptain, LeviathanDerived> = {
  id: "leviathan",
  version: "0.2.0",
  displayName: "Leviathan",
  kind: "captain",
  schema: leviathanCaptainSchema,
  createBlank: createBlankCaptain,
  validate,
  derive,
  describe: (captain) => (captain.name.trim() === "" ? "Unnamed captain" : captain.name),
};

export { leviathanCaptainSchema, createBlankCaptain } from "./character.js";
export type { LeviathanCaptain } from "./character.js";

export { derive, attributeCostOf, qualityCostOf, effectiveRankOf, roundCost, ROUND_COST_TO } from "./engine/derive.js";
export type { LeviathanDerived } from "./engine/derive.js";

export { validate } from "./engine/validate.js";

export {
  ATTRIBUTES,
  ATTRIBUTE_IDS,
  BALANCED_ATTRIBUTE_IDS,
  MAX_BALANCE_SPREAD,
  MAX_PURCHASABLE_RATING,
  MIN_RATING,
  costForRating,
  isPurchasableRating,
} from "./data/attributes.js";
export type { AttributeId, AttributeDef } from "./data/attributes.js";

// Faction data belongs to @personnel/setting -- import it from there. Only the
// type is surfaced here, because it appears in the captain schema.
export type { FactionId } from "@personnel/setting";

export { ESSENCE_COST, essenceCostFor } from "./data/essence.js";

export { recommendedRenownFor } from "./data/reputation.js";

// Reputation tables belong to @personnel/setting -- import them from there.
export type { ReputationPathId } from "@personnel/setting";

export { BUILD_MODES, EXPERIENCE_LEVELS, EXPERIENCE_LEVEL_IDS } from "./data/experience.js";
export type { ExperienceLevelId, ExperienceLevelDef, BuildMode } from "./data/experience.js";

export { QUALITIES, QUALITY_COST, QUALITY_TAGS, MAX_QUALITIES, qualityById } from "./data/qualities.js";
export type { QualityDef, QualityTag } from "./data/qualities.js";
